import { logger } from "@/utils/logger";
import { getDealerID } from '@/utils/dealerDataUtils';

export interface SessionMessage {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: string;
}

export interface SessionContact {
  Datestamp: string;
  Form_Type: 'Contact' | 'Offers' | 'FinanceV1' | 'Service' | 'Appointment' | 'Chat';
  Form_Content: { [key: string]: any };
}

export interface ChatSession {
  sessionId: string;
  chatId: string; // For webhook compatibility
  messages: SessionMessage[];
  createdAt: string;
  lastUpdated: string;
  Contact?: SessionContact;
  contactData: {
    name?: string;
    phone?: string;
    email?: string;
  };
  conversationSummary?: string;
}

export interface ChatPersistedState {
  messageBox: string;
  isExpanded: boolean;
  activeForm: string | null;
  formData: { [key: string]: string };
  showNewConversationDialog: boolean;
}

class SessionManager {
  private static instance: SessionManager;
  private readonly SESSION_KEY = 'chatbot_session';
  private readonly STATE_KEYS = {
    MESSAGE_BOX: 'messageBox',
    IS_EXPANDED: 'chatIsExpanded',
    ACTIVE_FORM: 'chatActiveForm',
    FORM_DATA: 'chatFormData',
    SHOW_NEW_CONVERSATION_DIALOG: 'chatShowNewConversationDialog',
  } as const;
  

  private constructor() {}

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  // Generate unique IDs
  private generateId(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  private generateChatId(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  // Session Management
  initializeSession(): ChatSession {
    try {
      const existingSession = localStorage.getItem(this.SESSION_KEY);
      if (existingSession) {
        try {
          const session: ChatSession = JSON.parse(existingSession);
          if (session.sessionId && session.createdAt) {
            return session;
          }
          logger.warn('⚠️ Session missing required fields, creating new');
          localStorage.removeItem(this.SESSION_KEY);
        } catch (parseError) {
          logger.warn('⚠️ Session parse failed, creating new');
          localStorage.removeItem(this.SESSION_KEY);
        }
      }

      // Create new session
      const newSession: ChatSession = {
        sessionId: this.generateId(),
        chatId: this.generateChatId(),
        messages: [],
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        contactData: {}
      };

      localStorage.setItem(this.SESSION_KEY, JSON.stringify(newSession));
      logger.log('🆕 Created new session:', newSession.sessionId);
      return newSession;
    } catch (error) {
      console.error('❌ Failed to initialize session:', error);
      return {
        sessionId: this.generateId(),
        chatId: this.generateChatId(),
        messages: [],
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        contactData: {}
      };
    }
  }

  // Removed - localStorage only mode
  private async loadMessagesFromDatabase(session: ChatSession): Promise<void> {
    logger.log('🔄 SESSION MANAGER: localStorage-only mode - no database loading');
  }
  
  // Async method kept for backward compatibility - sessions are localStorage-only
  async loadSessionFromSecureStorage(): Promise<ChatSession | null> {
    return null;
  }

  getCurrentSession(): ChatSession | null {
    try {
      const existingSession = localStorage.getItem(this.SESSION_KEY);
      if (!existingSession) return null;

      try {
        const parsed: ChatSession = JSON.parse(existingSession);
        if (parsed.sessionId && parsed.createdAt) {
          return parsed;
        }
        logger.warn('⚠️ getCurrentSession: Invalid session structure');
        return null;
      } catch (parseError) {
        console.error('❌ getCurrentSession: Parse error:', parseError);
        localStorage.removeItem(this.SESSION_KEY);
        return null;
      }
    } catch (error) {
      console.error('❌ Failed to get current session:', error);
      return null;
    }
  }

  /**
   * Async version of getCurrentSession (localStorage-only)
   */
  async getCurrentSessionAsync(): Promise<ChatSession | null> {
    return this.getCurrentSession();
  }

  /**
   * Save session (localStorage-only)
   *
   * IMPORTANT: We intentionally do NOT use SecureStorage for the 'chatbot_session' key.
   * Mixing encrypted + plain formats on the same key causes session churn and lost context.
   */
  private async saveSessionSecurely(session: ChatSession): Promise<void> {
    try {
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
    } catch (error) {
      console.error('❌ Failed to save session:', error);
    }
  }

  async logMessage(content: string, sender: 'user' | 'assistant'): Promise<void> {
    try {
      const session = await this.getCurrentSessionAsync();
      if (!session) {
        console.error('❌ No session found when trying to log message');
        return;
      }
      
      const newMessage: SessionMessage = {
        id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
        content,
        sender,
        timestamp: new Date().toISOString()
      };
      
      session.messages.push(newMessage);
      session.lastUpdated = new Date().toISOString();
      
      // Store session securely if it contains sensitive data
      this.saveSessionSecurely(session);
      
      // localStorage-only mode - no database sync
      
    } catch (error) {
      console.error('❌ Failed to log message to session:', error);
    }
  }

  updateContactData(data: { name?: string; phone?: string; email?: string }): void {
    try {
      const session = this.getCurrentSession();
      if (!session) {
        console.error('❌ No session found when trying to update contact data');
        return;
      }
      
      session.contactData = {
        ...session.contactData,
        ...data
      };
      session.lastUpdated = new Date().toISOString();
      
      // Store session with potential PII using secure storage when available
      this.saveSessionSecurely(session);
      
    } catch (error) {
      console.error('❌ Failed to update contact data:', error);
    }
  }

  updateConversationSummary(summary: string): void {
    try {
      const session = this.getCurrentSession();
      if (!session) {
        console.error('❌ No session found when trying to update conversation summary');
        return;
      }
      
      session.conversationSummary = summary;
      session.lastUpdated = new Date().toISOString();
      
      this.saveSessionSecurely(session);
      
    } catch (error) {
      console.error('❌ Failed to update conversation summary:', error);
    }
  }

  logContact(
    formType: 'Contact' | 'Offers' | 'FinanceV1' | 'Service' | 'Appointment' | 'Chat',
    formContent: { [key: string]: any }
  ): void {
    try {
      const session = this.getCurrentSession();
      if (!session) {
        console.error('❌ No session found when trying to log contact');
        return;
      }
      
      // Only set contact info if it doesn't exist yet (first contact)
      if (!session.Contact) {
        session.Contact = {
          Datestamp: new Date().toISOString(),
          Form_Type: formType,
          Form_Content: formContent
        };
      
      session.lastUpdated = new Date().toISOString();
      
      // Store session securely if it contains contact data
      this.saveSessionSecurely(session);
      } else {
        // Contact info already exists, skipping update
      }
      
    } catch (error) {
      console.error('❌ Failed to log contact to session:', error);
    }
  }

  clearSession(): void {
    try {
      localStorage.removeItem(this.SESSION_KEY);
    } catch (error) {
      console.error('❌ Failed to clear session:', error);
    }
  }

  // Chat State Management
  saveChatState(key: keyof typeof this.STATE_KEYS, value: any): void {
    try {
      localStorage.setItem(this.STATE_KEYS[key], JSON.stringify(value));
    } catch (error) {
      console.error(`Failed to save chat state for ${key}:`, error);
    }
  }

  getChatState<T>(key: keyof typeof this.STATE_KEYS, defaultValue: T): T {
    try {
      const stored = localStorage.getItem(this.STATE_KEYS[key]);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.error(`Failed to get chat state for ${key}:`, error);
      return defaultValue;
    }
  }

  clearChatState(): void {
    try {
      Object.values(this.STATE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error('Failed to clear chat state:', error);
    }
  }

  getInitialChatState(): ChatPersistedState {
    return {
      messageBox: this.getChatState('MESSAGE_BOX', ''),
      isExpanded: this.getChatState('IS_EXPANDED', false),
      activeForm: this.getChatState('ACTIVE_FORM', null),
      formData: this.getChatState('FORM_DATA', {}),
      showNewConversationDialog: this.getChatState('SHOW_NEW_CONVERSATION_DIALOG', false),
    };
  }

  // Removed - localStorage only mode
  async syncMessagesToDatabase(): Promise<void> {
    logger.log('💾 SESSION MANAGER: localStorage-only mode - no database sync');
  }
}

// Export singleton instance
export const sessionManager = SessionManager.getInstance();

// Backward compatibility exports
export const initializeSession = () => sessionManager.initializeSession();
export const getCurrentSession = () => sessionManager.getCurrentSession();
export const getCurrentSessionAsync = () => sessionManager.getCurrentSessionAsync();
export const logMessageToSession = async (content: string, sender: 'user' | 'assistant') => 
  await sessionManager.logMessage(content, sender);
export const logContactToSession = (
  formType: 'Contact' | 'Offers' | 'FinanceV1' | 'Service' | 'Appointment' | 'Chat',
  formContent: { [key: string]: any }
) => sessionManager.logContact(formType, formContent);
export const clearSession = () => sessionManager.clearSession();

type ChatStateKey = 'MESSAGE_BOX' | 'IS_EXPANDED' | 'ACTIVE_FORM' | 'FORM_DATA' | 'SHOW_NEW_CONVERSATION_DIALOG';

export const saveChatState = (key: ChatStateKey, value: any) =>
  sessionManager.saveChatState(key, value);
export const getChatState = <T>(key: ChatStateKey, defaultValue: T) =>
  sessionManager.getChatState(key, defaultValue);
export const clearChatState = () => sessionManager.clearChatState();
export const getInitialChatState = () => sessionManager.getInitialChatState();

// Removed - localStorage only mode